import type { RegistrationStatus } from "@/modules/events/types/eventDetailsApi"
import type { FunctionComponent } from "react"

import { differenceInCalendarDays, differenceInHours, differenceInMinutes, isAfter, isBefore } from "date-fns"
import { useParams } from "next/navigation"

import { Callout } from "@jobteaser/spark/components/Callout"

import { REGISTRATION_STATUS_NOT_ALLOWED, WITHOUT_REGISTRATION } from "@/modules/events/constants"
import { useEventDetailsData } from "@/modules/events/hooks/useEventDetailsData"
import { useTranslation } from "@/modules/i18n/components/useTranslation"

import styles from "./RegistrationReminder.module.css"

type RegistrationReminderProps = {
  registrationStatus: RegistrationStatus
}

export const RegistrationReminder: FunctionComponent<RegistrationReminderProps> = ({ registrationStatus }) => {
  const { t } = useTranslation(["event_details"])
  const { eventSlug } = useParams<{ eventSlug: string }>()
  const event = useEventDetailsData(eventSlug)

  if (!event) {
    return null
  }

  const { registrationDetails } = event

  if (registrationDetails.type === WITHOUT_REGISTRATION) {
    return null
  }

  const currentDate = new Date()
  const registrationStart = new Date(registrationDetails.startDate ? registrationDetails.startDate : "")
  const registrationEnd = new Date(
    registrationDetails.registrationDeadline ? registrationDetails.registrationDeadline : ""
  )

  const daysUntilRegistrationOpens = differenceInCalendarDays(registrationStart, currentDate)
  const daysUntilRegistrationEnds = differenceInCalendarDays(registrationEnd, currentDate)
  const hoursUntilRegistrationOpens = differenceInHours(registrationStart, currentDate)
  const minutesUntilRegistrationOpens = differenceInMinutes(registrationStart, currentDate, { roundingMethod: "ceil" })

  const THIRTY_DAYS = 30
  const FOURTEEN_DAYS = 14
  const ONE_DAY = 1
  const TWENTY_FOUR_HOURS = 24
  const TWO_HOURS = 2
  const ONE_HOUR = 1
  const ONE_MINUTE = 1

  const getReminderText = (): string => {
    if (registrationStatus.status === REGISTRATION_STATUS_NOT_ALLOWED) {
      return t("event_details.registration.not.allowed")
    }
    if (isBefore(currentDate, registrationStart)) {
      if (daysUntilRegistrationOpens >= THIRTY_DAYS) {
        return t("event_details.registration.opening.month")
      }
      if (daysUntilRegistrationOpens >= ONE_DAY && hoursUntilRegistrationOpens >= TWENTY_FOUR_HOURS) {
        return t("event_details.registration.opening.day", { count: daysUntilRegistrationOpens })
      }
      if (hoursUntilRegistrationOpens < TWENTY_FOUR_HOURS && hoursUntilRegistrationOpens >= TWO_HOURS) {
        return t("event_details.registration.opening.soon", { count: hoursUntilRegistrationOpens })
      }
      if (hoursUntilRegistrationOpens < TWO_HOURS && hoursUntilRegistrationOpens >= ONE_HOUR) {
        return t("event_details.registration.opening.hour")
      }
      if (hoursUntilRegistrationOpens < ONE_HOUR && minutesUntilRegistrationOpens > ONE_MINUTE) {
        return t("event_details.registration.opening.minutes", { count: minutesUntilRegistrationOpens })
      }
      if (minutesUntilRegistrationOpens === ONE_MINUTE) {
        return t("event_details.registration.opening.now")
      }
    }
    if (isAfter(currentDate, registrationStart) && isBefore(currentDate, registrationEnd)) {
      if (daysUntilRegistrationEnds > FOURTEEN_DAYS) {
        return ""
      }
      if (daysUntilRegistrationEnds >= ONE_DAY) {
        return t("event_details.registration.closing", { count: daysUntilRegistrationEnds })
      }
      if (daysUntilRegistrationEnds < ONE_DAY) {
        return t("event_details.registration.lastDay")
      }
    }
    if (isAfter(currentDate, registrationEnd)) {
      return t("event_details.registration.closed")
    }
    return ""
  }

  const isError = registrationStatus.status === REGISTRATION_STATUS_NOT_ALLOWED || isAfter(currentDate, registrationEnd)

  const text = getReminderText()

  if (text === "") {
    return null
  }

  return (
    <Callout
      className={styles.main}
      data-testid="event-details-registration-details-registration-reminder"
      variant={isError ? "error" : "info"}
    >
      {text}
    </Callout>
  )
}
